import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ═══════════════════════════════════════════════════════════════
   THUNKS
═══════════════════════════════════════════════════════════════ */

export const fetchLocations = createAsyncThunk(
  "location/fetchAll",
  async ({ page = 1, limit = 10, search = "", status = "" } = {}, thunkAPI) => {
    try {
      let url = `/api/locations?page=${page}&limit=${limit}`;
      if (search)          url += `&search=${encodeURIComponent(search)}`;
      if (status !== "")   url += `&status=${status}`;
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch locations"
      );
    }
  }
);

export const fetchLocationById = createAsyncThunk(
  "location/fetchById",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/locations/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch location");
    }
  }
);

export const createLocation = createAsyncThunk(
  "location/create",
  async (data, thunkAPI) => {
    try {
      const res = await api.post("/api/locations", data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to create location"
      );
    }
  }
);

export const updateLocation = createAsyncThunk(
  "location/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/api/locations/${id}`, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update location"
      );
    }
  }
);

export const toggleLocationStatus = createAsyncThunk(
  "location/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/locations/${id}/status`);
      return res.data.data;
    } catch {
      return thunkAPI.rejectWithValue("Failed to toggle location status");
    }
  }
);

export const deleteLocation = createAsyncThunk(
  "location/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/locations/${id}`);
      return id;
    } catch {
      return thunkAPI.rejectWithValue("Failed to delete location");
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════════ */
const locationSlice = createSlice({
  name: "location",
  initialState: {
    loading:         false,
    error:           null,
    success:         false,
    locations:       [],
    pagination:      {},
    currentLocation: null,
  },
  reducers: {
    resetLocationState: (state) => {
      state.loading = false;
      state.error   = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ── fetchAll ── */
      .addCase(fetchLocations.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading    = false;
        state.locations  = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchLocations.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchById ── */
      .addCase(fetchLocationById.pending,   (state)         => { state.loading = true; state.currentLocation = null; })
      .addCase(fetchLocationById.fulfilled, (state, action) => { state.loading = false; state.currentLocation = action.payload; })
      .addCase(fetchLocationById.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── create ── */
      .addCase(createLocation.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(createLocation.fulfilled, (state, action) => {
        state.loading    = false;
        state.success    = true;
        state.locations  = [action.payload, ...state.locations];
      })
      .addCase(createLocation.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── update ── */
      .addCase(updateLocation.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(updateLocation.fulfilled, (state, action) => {
        state.loading          = false;
        state.success          = true;
        state.currentLocation  = action.payload;
        const idx = state.locations.findIndex((l) => l._id === action.payload._id);
        if (idx !== -1) state.locations[idx] = action.payload;
      })
      .addCase(updateLocation.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── toggleStatus ── */
      .addCase(toggleLocationStatus.fulfilled, (state, action) => {
        const idx = state.locations.findIndex((l) => l._id === action.payload._id);
        if (idx !== -1) state.locations[idx].status = action.payload.status;
      })

      /* ── delete ── */
      .addCase(deleteLocation.fulfilled, (state, action) => {
        state.locations = state.locations.filter((l) => l._id !== action.payload);
      });
  },
});

export const { resetLocationState } = locationSlice.actions;
export default locationSlice.reducer;